import { useParams } from "react-router";
import { useRltWorkOrderShowQuery } from "@/services/backend/rlt/work-orders/api/work-order-api";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { WorkOrderPdf } from "@/services/backend/rlt/work-orders/components/work-order-pdf";
import { WorkOrder } from "@/services/backend/rlt/work-orders/types/work-order";
import { Separator } from "@/shared/components/ui/separator";
import { CancelWorkOrderDialog } from "@/services/backend/rlt/work-orders/components/cancel-work-order-dialog";
import { useWorkOrderStateGuard } from "@/routes/processes/[processId]/rlt/work-orders/_components/use-work-order-state-guard";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { FulfillmentStateBadge } from "@/services/backend/relationships/components/fulfillment-state-badge";
import { CompleteWorkOrderDialog } from "@/services/backend/rlt/work-orders/components/complete-work-order-dialog";
import { SetWorkOrderImmutableDialog } from "@/services/backend/rlt/work-orders/components/set-work-order-immutable-dialog";
import { BackToPlanningDialog } from "@/services/backend/rlt/work-orders/components/back-to-planning-dialog";
import { ConnectInvoiceExportDialogRlt } from "@/services/backend/connect/components/connect-invoice-export-rlt";
import { DocumentImmutableAlert } from "@/shared/bsm/core/document/components/ui/document-immutable-alert";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { WorkOrderBreadcrumbs } from "@/routes/processes/[processId]/rlt/work-orders/_components/work-order-breadcrumbs";

export function WorkOrderCompletionRoute() {
  const { workOrderId } = useParams();
  const {
    data: workOrder,
    isLoading,
    error,
  } = useRltWorkOrderShowQuery({
    id: workOrderId!,
  });

  useWorkOrderStateGuard(
    workOrder,
    FulfillmentState.Executed,
    FulfillmentState.PartiallyExecuted,
    FulfillmentState.Cancelled,
    FulfillmentState.Completed,
  );

  const breadcrumbs = workOrder && (
    <WorkOrderBreadcrumbs workOrder={workOrder} segment="completion">
      {t("Nachbereitung")}
    </WorkOrderBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {workOrder && (
        <PersistenceContextProvider>
          <Page workOrder={workOrder} />
        </PersistenceContextProvider>
      )}
    </ProcessPageMainTemplate>
  );
}

function Page({ workOrder }: { workOrder: WorkOrder }) {
  const links = [
    {
      to: "base-data",
      label: t("Daten"),
    },
    {
      to: "pricing",
      label: t("Preise"),
    },
    {
      to: "calculation",
      label: t("Kalkulation"),
    },
    {
      to: "treatment",
      label: t("Behandlung"),
    },
    {
      to: `documentation/${workOrder.documentationId}`,
      label: t("Dokumentation"),
    },
    {
      to: `last-minute-risk-analysis/${workOrder.lastMinuteRiskAnalysisId}`,
      label: t("LMRA"),
    },
    {
      to: "files",
      label: t("Dateien"),
    },
  ];

  return (
    <DocumentPage>
      <DocumentImmutableAlert document={workOrder} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink key={link.to} to={link.to}>
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <Separator orientation="vertical" className="max-h-8" />
        <FulfillmentStateBadge state={workOrder.state} />
        <DocumentPageActions>
          {!workOrder.immutable && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <Separator orientation="vertical" className="min-h-8" />
              <SetWorkOrderImmutableDialog workOrder={workOrder} />
              <WorkOrderPdf workOrderId={workOrder.id} />
              <ConnectInvoiceExportDialogRlt workOrder={workOrder} />
              <Separator orientation="vertical" className="min-h-8" />
              {[
                FulfillmentState.Completed,
                FulfillmentState.Cancelled,
              ].includes(workOrder.state) ? (
                <BackToPlanningDialog workOrder={workOrder} />
              ) : (
                <>
                  <CancelWorkOrderDialog workOrder={workOrder} />
                  <CompleteWorkOrderDialog workOrder={workOrder} />
                </>
              )}
            </>
          )}
          {workOrder.immutable && <WorkOrderPdf workOrderId={workOrder.id} />}
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
